import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Html5Qrcode } from "html5-qrcode";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { 
  Car, 
  ArrowLeft,
  Check,
  X,
  AlertTriangle,
  Building2,
  User,
  Phone,
  AlertCircle,
  CheckCircle2
} from "lucide-react";

export default function ScannerPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const html5QrcodeRef = useRef(null);
  const scannerContainerRef = useRef(null);
  
  const [locais, setLocais] = useState([]);
  const [selectedLocal, setSelectedLocal] = useState("");
  const [localInfo, setLocalInfo] = useState(null);
  const [scanResult, setScanResult] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState(null); // { type: 'success' | 'error', text: string }

  // Show message for 4 seconds then auto-hide
  const showMessage = useCallback((type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 4000);
  }, []);

  // Screen Wake Lock
  useEffect(() => {
    let wakeLock = null;
    
    const requestWakeLock = async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeLock = await navigator.wakeLock.request('screen');
          console.log('Screen Wake Lock activated');
        }
      } catch (err) {
        console.log('Wake Lock error:', err);
      }
    };

    if (isScanning) {
      requestWakeLock();
    }

    return () => {
      if (wakeLock) {
        wakeLock.release();
      }
    };
  }, [isScanning]);

  // Fetch locais
  useEffect(() => {
    const fetchLocais = async () => {
      try {
        const response = await axios.get(`${API}/locais`);
        setLocais(response.data);
        if (response.data.length === 1) {
          setSelectedLocal(response.data[0].id);
        }
      } catch (error) {
        showMessage('error', 'Erro ao carregar locais');
      }
    };
    fetchLocais();
  }, [showMessage]);

  // Fetch local info for real-time display
  useEffect(() => {
    if (!selectedLocal) return;
    
    const fetchLocalInfo = async () => {
      try {
        const response = await axios.get(`${API}/dashboard/stats`);
        const local = response.data.locais_stats?.find(l => l.local_id === selectedLocal);
        setLocalInfo(local);
      } catch (error) {
        console.error("Error fetching local info:", error);
      }
    };

    fetchLocalInfo();
    const interval = setInterval(fetchLocalInfo, 3000);
    return () => clearInterval(interval);
  }, [selectedLocal]);

  // Initialize scanner
  const startScanner = useCallback(async () => {
    if (!selectedLocal || !scannerContainerRef.current) return;

    try {
      // Stop existing scanner if running
      if (html5QrcodeRef.current) {
        try {
          if (html5QrcodeRef.current.isScanning) {
            await html5QrcodeRef.current.stop();
          }
          html5QrcodeRef.current.clear();
        } catch (e) {
          console.log("Cleanup error:", e);
        }
      }

      // Create new scanner instance
      html5QrcodeRef.current = new Html5Qrcode("qr-reader");
      
      await html5QrcodeRef.current.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0
        },
        handleScan,
        () => {} // Ignore errors
      );
      
      setIsScanning(true);
    } catch (err) {
      console.error("Scanner error:", err);
      showMessage('error', 'Erro ao iniciar câmara. Verifique as permissões.');
    }
  }, [selectedLocal]);

  // Start scanner when local is selected
  useEffect(() => {
    if (selectedLocal && !scanResult) {
      startScanner();
    }

    return () => {
      if (html5QrcodeRef.current && html5QrcodeRef.current.isScanning) {
        html5QrcodeRef.current.stop().catch(console.error);
      }
    };
  }, [selectedLocal, scanResult, startScanner]);

  const handleScan = useCallback(async (decodedText) => {
    if (processing || scanResult) return;
    
    setProcessing(true);
    
    try {
      // Pause scanner during processing
      if (html5QrcodeRef.current && html5QrcodeRef.current.isScanning) {
        await html5QrcodeRef.current.pause(true);
      }

      const response = await axios.post(`${API}/scanner/validate`, {
        qr_code_hash: decodedText,
        local_id: selectedLocal
      });
      
      setScanResult(response.data);
      
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Erro ao validar QR Code');
      // Resume scanner on error
      if (html5QrcodeRef.current) {
        try {
          await html5QrcodeRef.current.resume();
        } catch (e) {
          console.log("Resume error:", e);
        }
      }
    } finally {
      setProcessing(false);
    }
  }, [selectedLocal, processing, scanResult, showMessage]);

  const confirmEntry = async () => {
    if (!scanResult?.viatura) return;
    
    setProcessing(true);
    try {
      await axios.post(`${API}/scanner/confirm-entry`, {
        qr_code_hash: scanResult.viatura.qr_code_hash,
        local_id: selectedLocal
      });
      showMessage('success', `Entrada confirmada - ${scanResult.viatura.matricula}`);
      await resetAndRestartScanner();
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Erro ao confirmar entrada');
    } finally {
      setProcessing(false);
    }
  };

  const confirmExit = async () => {
    if (!scanResult?.viatura) return;
    
    setProcessing(true);
    try {
      await axios.post(`${API}/scanner/confirm-exit`, {
        qr_code_hash: scanResult.viatura.qr_code_hash,
        local_id: selectedLocal
      });
      showMessage('success', `Saída confirmada - ${scanResult.viatura.matricula}`);
      await resetAndRestartScanner();
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Erro ao confirmar saída');
    } finally {
      setProcessing(false);
    }
  };

  const forceExit = async () => {
    if (!scanResult?.viatura) return;
    
    setProcessing(true);
    try {
      await axios.post(`${API}/scanner/force-exit`, {
        qr_code_hash: scanResult.viatura.qr_code_hash,
        local_id: selectedLocal
      });
      showMessage('success', `Saída forçada registada - ${scanResult.viatura.matricula}`);
      await resetAndRestartScanner();
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Erro ao forçar saída');
    } finally {
      setProcessing(false);
    }
  };

  const resetAndRestartScanner = async () => {
    setScanResult(null);
    
    // Small delay to ensure state is cleared
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Restart scanner
    await startScanner();
  };

  const cancelScan = async () => {
    setScanResult(null);
    await startScanner();
  };

  return (
    <div className="min-h-screen bg-[#0F172A] flex flex-col">
      {/* Message Banner */}
      {message && (
        <div 
          className={`fixed top-0 left-0 right-0 z-[100] px-4 py-3 flex items-center justify-center gap-2 ${
            message.type === 'success' ? 'bg-[#00C853]' : 'bg-[#D50000]'
          }`}
          data-testid="message-banner"
        >
          {message.type === 'success' ? (
            <CheckCircle2 className="w-5 h-5 text-white" />
          ) : (
            <AlertCircle className="w-5 h-5 text-white" />
          )}
          <span className="text-white font-medium">{message.text}</span>
        </div>
      )}

      {/* Header */}
      <header className={`sticky ${message ? 'top-12' : 'top-0'} z-30 bg-[#0F172A] border-b border-white/10 px-4 py-3 transition-all duration-200`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/dashboard">
              <Button variant="ghost" size="icon" className="text-white hover:bg-white/10" data-testid="back-btn">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#0047FF] rounded-lg flex items-center justify-center">
                <Car className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-bold text-white">Scanner</span>
            </div>
          </div>
          
          {localInfo && (
            <Badge 
              className={`text-sm font-medium ${
                localInfo.disponivel > 0 ? 'bg-[#00C853]' : 'bg-[#D50000]'
              }`}
              data-testid="local-capacity-badge"
            >
              {localInfo.ocupacao_atual} / {localInfo.capacidade_total}
            </Badge>
          )}
        </div>
      </header>

      {/* Local Selector */}
      {!selectedLocal && (
        <div className="flex-1 p-6 flex flex-col items-center justify-center">
          <Card className="w-full max-w-md bg-white/5 border-white/10">
            <CardContent className="p-6 space-y-4">
              <h2 className="text-xl font-bold text-white text-center">Selecione o Local</h2>
              <Select value={selectedLocal} onValueChange={setSelectedLocal}>
                <SelectTrigger className="w-full bg-white/10 border-white/20 text-white" data-testid="local-select">
                  <SelectValue placeholder="Escolha um local" />
                </SelectTrigger>
                <SelectContent>
                  {locais.map((local) => (
                    <SelectItem key={local.id} value={local.id}>
                      {local.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Scanner View */}
      {selectedLocal && !scanResult && (
        <div className="flex-1 flex flex-col">
          {/* Local selector (compact) */}
          <div className="px-4 py-2">
            <Select value={selectedLocal} onValueChange={setSelectedLocal}>
              <SelectTrigger className="w-full bg-white/10 border-white/20 text-white" data-testid="local-select-compact">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {locais.map((local) => (
                  <SelectItem key={local.id} value={local.id}>
                    {local.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* QR Reader */}
          <div className="flex-1 relative" ref={scannerContainerRef}>
            <div id="qr-reader" className="w-full h-full" data-testid="qr-reader"></div>
            
            {/* Overlay */}
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute inset-0 bg-black/50" style={{
                clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 100%, 0 0, 15% 25%, 15% 75%, 85% 75%, 85% 25%, 15% 25%)'
              }} />
              
              {/* Scan frame */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64">
                <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-[#0047FF]" />
                <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-[#0047FF]" />
                <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-[#0047FF]" />
                <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-[#0047FF]" />
                
                {/* Scan line */}
                <div className="absolute top-0 left-4 right-4 h-0.5 bg-[#0047FF] scan-line" />
              </div>
            </div>
            
            {/* Instructions */}
            <div className="absolute bottom-8 left-0 right-0 text-center">
              <p className="text-white/80 text-sm">
                {processing ? "A processar..." : "Aponte para o QR Code da viatura"}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Scan Result */}
      {scanResult && (
        <div className={`flex-1 flex flex-col ${
          scanResult.action === 'entry' ? 'bg-[#00C853]' :
          scanResult.action === 'entry_warning' ? 'bg-[#F59E0B]' :
          scanResult.action === 'exit' ? 'bg-[#0047FF]' :
          scanResult.action === 'denied' ? 'bg-[#D50000]' :
          scanResult.action === 'incident' ? 'bg-[#FF9800]' :
          'bg-[#64748B]'
        }`} data-testid="scan-result">
          
          <div className="flex-1 flex flex-col items-center justify-center p-6 text-white">
            {/* Status Icon */}
            <div className="w-24 h-24 rounded-full bg-white/20 flex items-center justify-center mb-6">
              {scanResult.action === 'entry' && <Check className="w-12 h-12" />}
              {scanResult.action === 'entry_warning' && <AlertTriangle className="w-12 h-12" />}
              {scanResult.action === 'exit' && <Check className="w-12 h-12" />}
              {scanResult.action === 'denied' && <X className="w-12 h-12" />}
              {scanResult.action === 'incident' && <AlertTriangle className="w-12 h-12" />}
              {scanResult.action === 'none' && <X className="w-12 h-12" />}
            </div>

            {/* Message */}
            <h1 className="text-3xl font-bold text-center mb-2" data-testid="scan-message">
              {scanResult.message}
            </h1>

            {/* Warning subtitle for entry_warning */}
            {scanResult.action === 'entry_warning' && (
              <div className="text-center mt-2 mb-4" data-testid="capacity-warning">
                <p className="text-white/90 text-lg font-semibold">
                  Lotação: {scanResult.local_ocupacao_atual} / {scanResult.local_capacidade_total}
                </p>
                <p className="text-white/80 text-sm mt-1">
                  A empresa tem quota disponível mas o local está lotado.
                </p>
                <p className="text-white/80 text-sm">
                  Pretende permitir a entrada?
                </p>
              </div>
            )}

            {/* Vehicle Info */}
            {scanResult.viatura && (
              <div className="w-full max-w-md mt-6 space-y-4">
                <Card className="bg-white/10 border-white/20">
                  <CardContent className="p-4">
                    <div className="text-center mb-4">
                      <p className="text-4xl font-bold tracking-wider" data-testid="scan-matricula">
                        {scanResult.viatura.matricula}
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 opacity-70" />
                        <span className="opacity-90">{scanResult.empresa_nome}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 opacity-70" />
                        <span className="opacity-90">{scanResult.viatura.nome_condutor}</span>
                      </div>
                      {scanResult.viatura.contacto_condutor && (
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4 opacity-70" />
                          <span className="opacity-90">{scanResult.viatura.contacto_condutor}</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Quota Info */}
                <Card className="bg-white/10 border-white/20">
                  <CardContent className="p-4">
                    <p className="text-xs font-semibold tracking-wider uppercase opacity-70 mb-2">Quota da Empresa</p>
                    <div className="flex justify-between items-center">
                      <span className="text-2xl font-bold">
                        {scanResult.quota_ocupada} / {scanResult.quota_total}
                      </span>
                      <Badge className="bg-white/20">
                        {scanResult.quota_disponivel} disponíveis
                      </Badge>
                    </div>
                  </CardContent>
                </Card>

                {/* Local Capacity Info */}
                {scanResult.local_capacidade_total > 0 && (
                  <Card className={`border-white/20 ${scanResult.local_lotado ? 'bg-white/25' : 'bg-white/10'}`}>
                    <CardContent className="p-4">
                      <p className="text-xs font-semibold tracking-wider uppercase opacity-70 mb-2">Lotação do Local</p>
                      <div className="flex justify-between items-center">
                        <span className="text-2xl font-bold">
                          {scanResult.local_ocupacao_atual} / {scanResult.local_capacidade_total}
                        </span>
                        <Badge className={scanResult.local_lotado ? 'bg-red-500/80' : 'bg-white/20'}>
                          {scanResult.local_lotado ? 'LOTADO' : `${scanResult.local_capacidade_total - scanResult.local_ocupacao_atual} livres`}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="p-6 space-y-3">
            {scanResult.action === 'entry' && (
              <Button
                onClick={confirmEntry}
                disabled={processing}
                className="w-full h-14 bg-white text-[#00C853] hover:bg-white/90 text-lg font-bold"
                data-testid="confirm-entry-btn"
              >
                {processing ? "A confirmar..." : "Confirmar Entrada"}
              </Button>
            )}

            {scanResult.action === 'entry_warning' && (
              <>
                <Button
                  onClick={confirmEntry}
                  disabled={processing}
                  className="w-full h-14 bg-white text-[#F59E0B] hover:bg-white/90 text-lg font-bold"
                  data-testid="allow-entry-warning-btn"
                >
                  {processing ? "A confirmar..." : "Permitir Entrada"}
                </Button>
                <Button
                  onClick={cancelScan}
                  className="w-full h-14 bg-white/20 text-white hover:bg-white/30 text-lg font-bold border-2 border-white/40"
                  data-testid="cancel-entry-warning-btn"
                >
                  Cancelar Entrada
                </Button>
              </>
            )}

            {scanResult.action === 'exit' && (
              <Button
                onClick={confirmExit}
                disabled={processing}
                className="w-full h-14 bg-white text-[#0047FF] hover:bg-white/90 text-lg font-bold"
                data-testid="confirm-exit-btn"
              >
                {processing ? "A confirmar..." : "Confirmar Saída"}
              </Button>
            )}

            {scanResult.requires_exit_first && (
              <Button
                onClick={forceExit}
                disabled={processing}
                className="w-full h-14 bg-white text-[#FF9800] hover:bg-white/90 text-lg font-bold"
                data-testid="force-exit-btn"
              >
                {processing ? "A processar..." : "Forçar Saída (Criar Incidência)"}
              </Button>
            )}

            <Button
              onClick={cancelScan}
              variant="outline"
              className="w-full h-12 border-white/30 text-white hover:bg-white/10"
              data-testid="cancel-scan-btn"
            >
              Cancelar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
